import { inject, Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { PortalLayoutResolveService } from 'src/app/admin/ad-admin/ad-layout/portal-layout-resolve.service';
import { TfService } from 'src/app/tf/tf-service.service';
import {
  DEFAULT_SHOPPING_BASKET_LAYOUT_CONFIG,
  ShoppingBasketLayoutConfig,
  normalizeShoppingBasketLayoutConfig,
} from '../model/shopping-basket-layout.model';

@Injectable({
  providedIn: 'root',
})
export class ShoppingBasketLayoutConfigService {
  private readonly portalLayoutResolve = inject(PortalLayoutResolveService);
  private readonly injector = inject(Injector);

  private readonly configSubject = new BehaviorSubject<ShoppingBasketLayoutConfig | null>(null);
  readonly config$ = this.configSubject.asObservable();

  private loaded = false;
  private loadInFlight: Observable<ShoppingBasketLayoutConfig> | null = null;

  constructor() {
    this.portalLayoutResolve.loaded$.subscribe((loaded) => {
      if (!loaded) {
        this.clearCache();
        return;
      }
      this.syncFromPortalCache({ allowDefault: true });
    });
  }

  ensureLoaded(): void {
    const hasPortalEntry = !!this.portalLayoutResolve.getEntry('shopping-basket');
    if (this.loaded && hasPortalEntry) {
      return;
    }
    if (this.loaded && !hasPortalEntry && this.isAuthenticated()) {
      this.loaded = false;
      this.loadInFlight = null;
    }
    if (this.loaded) {
      return;
    }
    this.load().subscribe();
  }

  reload(): Observable<ShoppingBasketLayoutConfig> {
    this.loaded = false;
    this.loadInFlight = null;
    return this.portalLayoutResolve
      .reloadSession(['shopping-basket'])
      .pipe(switchMap(() => this.load()));
  }

  getSnapshot(): ShoppingBasketLayoutConfig {
    return this.configSubject.value ?? DEFAULT_SHOPPING_BASKET_LAYOUT_CONFIG;
  }

  /** True once a layout config (custom or default) has been resolved. */
  isReady(): boolean {
    return this.loaded && this.configSubject.value !== null;
  }

  clearCache(): void {
    this.loaded = false;
    this.loadInFlight = null;
    this.configSubject.next(null);
  }

  /**
   * Apply shopping-basket entry from portal layout cache.
   * When `allowDefault` is true (portal batch finished), fall back to defaults if no entry exists.
   */
  syncFromPortalCache(options?: { allowDefault?: boolean }): void {
    const entry = this.portalLayoutResolve.getEntry('shopping-basket');
    if (entry) {
      const config = normalizeShoppingBasketLayoutConfig(entry.payload);
      this.configSubject.next(config);
      this.loaded = true;
      this.loadInFlight = null;
      return;
    }

    if (!this.isAuthenticated() || options?.allowDefault) {
      this.configSubject.next(DEFAULT_SHOPPING_BASKET_LAYOUT_CONFIG);
      this.loaded = true;
      this.loadInFlight = null;
    }
  }

  private load(): Observable<ShoppingBasketLayoutConfig> {
    if (this.loadInFlight) {
      return this.loadInFlight;
    }

    this.loadInFlight = this.loadFromResolvedLayout().pipe(shareReplay(1));
    return this.loadInFlight;
  }

  private loadFromResolvedLayout(): Observable<ShoppingBasketLayoutConfig> {
    const cached = this.portalLayoutResolve.getEntry('shopping-basket');
    if (cached) {
      const config = normalizeShoppingBasketLayoutConfig(cached.payload);
      return of(config).pipe(
        tap((resolved) => {
          this.configSubject.next(resolved);
          this.loaded = true;
        }),
      );
    }

    // Wait for portal resolve-batch instead of flashing the default layout early.
    return this.portalLayoutResolve.watchComponent('shopping-basket').pipe(
      take(1),
      map((entry) =>
        entry
          ? normalizeShoppingBasketLayoutConfig(entry.payload)
          : DEFAULT_SHOPPING_BASKET_LAYOUT_CONFIG,
      ),
      tap((config) => {
        this.configSubject.next(config);
        this.loaded = true;
        this.loadInFlight = null;
      }),
    );
  }

  private isAuthenticated(): boolean {
    return this.injector.get(TfService).getCurrentAuthInfo().isAuthenticated;
  }
}
